import React, { useCallback, useMemo, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { poisFromSavedPOISelector } from 'selectors/geo-selectors';
import { selectedCitySelector } from 'selectors/app-selector';
import Scroller from 'components/v2/scroller';
import cx from 'classnames';
import {
  newBookingPageStateKey,
  newBookingPageStateModeValues,
  ROUTES_V2_PATHS,
} from 'constants/route-constants';
import { useHistory } from 'react-router';
import NewBookingDuck from 'ducks/new-booking-duck';
import { useDispatch, useSelector } from 'react-redux';
import { firstDeliverySelector } from 'selectors/new-booking-selectors';
import _get from 'lodash/get';
import expandIcon from 'public/images/down-arrow.svg';
import deliverToSavedPOI from 'public/images/deliverToSavedPOI.svg';
import I18n from 'i18n-js';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import DivButton from 'components/v2/div-button/div-button';
import { DEFAULT_MAX_SAVED_POIS_DISPLAY } from 'constants/booking-constants';
import ContextualOnboarding from 'components/v2/contextual-onboarding';
import ScribeDuck from 'ducks/scribe-duck';
import SavedPoiUnitCard from './saved-poi-unit-card';
import styles from './suggested-places.less';
import savedPoiKey from './helper/saved-poi-key';
import OnboardingContent from '../../../components/v2/contextual-onboarding/onboarding-content';

const deliverToTitleElemID = 'deliverToTitle';
const savedPOIOnboardingID = 'deliverToSavedPOI';

const useSuggestedPlaces = () => {
  const savedPOIs = useSelector(poisFromSavedPOISelector);
  const city = useSelector(selectedCitySelector);

  const history = useHistory();
  const dispatch = useDispatch();
  const delivery = useSelector(firstDeliverySelector);

  const onlySameCitySavedPOIs = useMemo(
    () => {
      const currentCityId = _get(city, 'cityID').toString();
      const filtedSavedPOIs = savedPOIs
        ? savedPOIs.filter(
            poi => _get(poi, ['grabtaxi', 'city_id']) === currentCityId,
          )
        : [];
      return filtedSavedPOIs.slice(0, DEFAULT_MAX_SAVED_POIS_DISPLAY);
    },
    [city, savedPOIs],
  );

  const onClickSavedPOIsUnitCard = useCallback(
    (savedPoi, index) => () => {
      const deliveryDetails = _get(savedPoi, 'sp_ext_info');
      const contactName = _get(deliveryDetails, 'contact_name');
      const contactNumber = _get(deliveryDetails, 'contact_number');
      const addressDetail = _get(deliveryDetails, 'address_details');

      history.push(ROUTES_V2_PATHS.newBooking, {
        [newBookingPageStateKey.INIT_MODE]:
          newBookingPageStateModeValues.SINGLE,
      });
      const deliveryID = _get(delivery, 'id');

      dispatch(
        ScribeDuck.creators.scribeExpressSavedPOISelectPOIFromDeliverTo(
          index,
          onlySameCitySavedPOIs.length,
          'Predict',
        ),
      );
      if (_get(delivery, 'detail.pickUp.poi.id')) {
        dispatch(
          NewBookingDuck.creators.openDetailOverlay(
            deliveryID,
            newBookingOverlayType.serviceConfiguration,
          ),
        );
      }
      dispatch(
        NewBookingDuck.creators.setDropOffs(deliveryID, [
          {
            name: contactName,
            phone: contactNumber,
            locationNote: addressDetail,
            poi: savedPoi,
          },
        ]),
      );
    },
    [delivery, onlySameCitySavedPOIs],
  );

  return [onlySameCitySavedPOIs, onClickSavedPOIsUnitCard];
};

export const SuggestedPlacesHorizontal = () => {
  const [savedPOIs, onClickSavedPOIsUnitCard] = useSuggestedPlaces();

  if (_isEmpty(savedPOIs)) {
    return null;
  }

  return (
    <div>
      <div className={styles.sectionHeaderHorizontal}>
        <span id={deliverToTitleElemID} className={styles.title}>
          {`${I18n.t(
            'phrase.savedPoi.express_web_savedpoi_heading_deliver_to_suggested_places',
          )} (${savedPOIs.length})`}
        </span>
      </div>
      <Scroller
        className={styles.scroller}
        contentClassName={cx(styles.childrenWrapper, {
          [styles.single]: !savedPOIs || savedPOIs.length <= 1,
        })}
      >
        {savedPOIs &&
          savedPOIs.map((poi, index) => (
            <SavedPoiUnitCard
              key={savedPoiKey(poi)}
              savedPoi={poi}
              onClickSavedPOIsUnitCard={onClickSavedPOIsUnitCard}
              index={index}
            />
          ))}
      </Scroller>
    </div>
  );
};

SuggestedPlacesHorizontal.propTypes = {};

export const SuggestedPlacesVertical = () => {
  const [savedPOIs, onClickSavedPOIsUnitCard] = useSuggestedPlaces();

  const [viewAll, setViewAll] = useState(false);

  const onClickViewMore = useCallback(
    () => {
      setViewAll(true);
    },
    [setViewAll],
  );

  if (_isEmpty(savedPOIs)) {
    return null;
  }

  return (
    <div>
      <div className={styles.sectionHeaderVertical}>
        <span id={deliverToTitleElemID} className={styles.title}>
          {`${I18n.t(
            'phrase.savedPoi.express_web_savedpoi_heading_deliver_to_suggested_places',
          )} (${savedPOIs.length})`}
        </span>
      </div>
      <div className={styles.suggestPlacesVertical}>
        {(viewAll ? savedPOIs : savedPOIs.slice(0, 2)).map((poi, index) => (
          <div
            className={styles.suggestPlacesVerticalItem}
            key={savedPoiKey(poi)}
          >
            <SavedPoiUnitCard
              key={savedPoiKey(poi)}
              savedPoi={poi}
              onClickSavedPOIsUnitCard={onClickSavedPOIsUnitCard}
              index={index}
            />
          </div>
        ))}
        {!viewAll &&
          savedPOIs.length > 2 && (
            <DivButton
              onClick={onClickViewMore}
              className={styles.viewMoreButton}
            >
              View more <img src={expandIcon} alt="expand icon" />
            </DivButton>
          )}
      </div>
    </div>
  );
};

SuggestedPlacesVertical.propTypes = {};

export const SuggestedPlacesOnboarding = () => (
  <ContextualOnboarding
    elementID={deliverToTitleElemID}
    onboardingID={savedPOIOnboardingID}
    onboardingVisualPlacement={'bottomLeft'}
    onboardingVisual={
      <OnboardingContent
        width={240}
        imgUrl={deliverToSavedPOI}
        title={I18n.t('v2.savedPOI.onboarding.deliverToTitle')}
        description={I18n.t('v2.savedPOI.onboarding.deliverToDescription')}
      />
    }
    maxOnboardingTimes={1}
  />
);
